// Rule-based room placement advisor (Stage 9c). Scores how well a position suits
// a room type by matching the position's time-of-day sun (morning/midday/
// afternoon band-hours from the banded heatmap) against per-type profiles
// (room_sun_profiles.json): a bedroom wants morning sun and dislikes afternoon
// overheating, a living room wants day/afternoon sun, etc. Pure JS, no DOM —
// unit-tested with Vitest. Advisory only ("ориентир, не норматив").
//
// All polygons/coordinates are in the PLOT frame (the heatmap's frame). The
// House-mode caller transforms its local plan into the plot frame first.

import profilesPack from "../rules/ru/room_sun_profiles.json"

export function profileFor(type) {
  return profilesPack.types?.[type] ?? profilesPack.default
}

// Per-cell band hours at a plot-frame point, or null when the point is outside
// the heatmap grid. Bands are hours of direct sun in that part of the day.
export function bandHoursAt(heatmap, xPlot, yPlot) {
  const { bandMinutes, cols, rows, resolutionM, bb } = heatmap ?? {}
  if (!bandMinutes) return null
  const c = Math.floor((xPlot - bb.minX) / resolutionM)
  const r = Math.floor((yPlot - bb.minY) / resolutionM)
  if (c < 0 || c >= cols || r < 0 || r >= rows) return null
  const idx = r * cols + c
  return {
    morning: bandMinutes.morning[idx] / 60,
    midday: bandMinutes.midday[idx] / 60,
    afternoon: bandMinutes.afternoon[idx] / 60,
  }
}

// Average band hours over the cells whose centres fall inside a plot-frame
// polygon (the room footprint). Returns { morning, midday, afternoon, total,
// cells } — cells is 0 when none land inside (room smaller than a grid cell).
export function bandHoursInPolygon(heatmap, polygonPlot) {
  const { bandMinutes, cols, rows, resolutionM, bb } = heatmap ?? {}
  const zero = { morning: 0, midday: 0, afternoon: 0, total: 0, cells: 0 }
  if (!bandMinutes) return zero
  let mo = 0, mi = 0, af = 0, n = 0
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const xM = bb.minX + (c + 0.5) * resolutionM
      const yM = bb.minY + (r + 0.5) * resolutionM
      if (!pointInPolygon(xM, yM, polygonPlot)) continue
      const idx = r * cols + c
      mo += bandMinutes.morning[idx]
      mi += bandMinutes.midday[idx]
      af += bandMinutes.afternoon[idx]
      n++
    }
  }
  if (n === 0) return zero
  return { morning: mo / n / 60, midday: mi / n / 60, afternoon: af / n / 60, total: (mo + mi + af) / n / 60, cells: n }
}

// Weighted suitability score (in "matched hours") of a position's band hours for
// a profile. Negative weights (e.g. a bedroom's afternoon) penalise overheating,
// so a morning-rich spot beats an afternoon-rich one for a bedroom.
export function scorePlacement(bandHours, profile) {
  const w = profile?.weights ?? {}
  return (w.morning ?? 0) * (bandHours.morning ?? 0) +
    (w.midday ?? 0) * (bandHours.midday ?? 0) +
    (w.afternoon ?? 0) * (bandHours.afternoon ?? 0)
}

// Fit verdict for a room of `type` at a position with the given band hours:
//   { score, good, label } where `good` means the score clears the profile's
//   goodFitScore threshold and `label` is the type's recommendation text.
export function fitFor(bandHours, type) {
  const profile = profileFor(type)
  const score = scorePlacement(bandHours, profile)
  return { score, good: score >= (profile.goodFitScore ?? 0), label: profile.label }
}

function pointInPolygon(x, y, polygon) {
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1]
    const xj = polygon[j][0], yj = polygon[j][1]
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}
